import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  CameraIcon,
  TrashIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';

const CATEGORIES = [
  { key: 'effort',           label: 'Effort' },
  { key: 'hitting',          label: 'Hitting' },
  { key: 'pitching',         label: 'Pitching' },
  { key: 'running',          label: 'Running' },
  { key: 'fielding_infield', label: 'Fielding – Infield' },
  { key: 'fielding_outfield',label: 'Fielding – Outfield' },
];

const getInitials = (name) =>
  String(name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

const playerLabel = (reg) => {
  const name = reg.player?.name || reg.user?.name || 'Unknown Player';
  return reg.tryoutNumber != null ? `#${reg.tryoutNumber} – ${name}` : `? – ${name}`;
};

const ScoreSlider = ({ label, value, onChange }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <span className="text-sm font-medium text-slate-300">{label}</span>
      <span className="text-sm font-semibold text-white w-8 text-right">
        {value != null ? value : '–'}
      </span>
    </div>
    <div className="flex items-center gap-3">
      <input
        type="range"
        min={1}
        max={10}
        step={1}
        value={value ?? 5}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex-1 accent-[var(--theme-primary)] h-2 rounded-full cursor-pointer"
      />
      <div className="flex gap-0.5">
        {[1,2,3,4,5,6,7,8,9,10].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={`w-6 h-6 rounded text-xs font-semibold transition-colors ${
              value === n
                ? 'text-white'
                : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
            }`}
            style={value === n ? { backgroundColor: 'var(--theme-primary)' } : {}}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  </div>
);

const TryoutScoring = () => {
  const { user } = useAuth();
  const token = user?.token || localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [eventId, setEventId] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [registrants, setRegistrants] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [scoredIds, setScoredIds] = useState(new Set());
  const [draft, setDraft] = useState({ scores: {}, overall: null, notes: '', photoUrl: null });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [numberInput, setNumberInput] = useState('');
  const photoInputRef = useRef(null);
  // Tracks whether the evaluator uploaded a photo for the current player in this session.
  // Reset each time we navigate to a new player so stale-GET protection doesn't
  // carry a previous player's photo forward.
  const photoUploadedRef = useRef(false);

  // Parse eventId from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const eId = parseInt(params.get('eventId'), 10);
    if (eId) setEventId(eId);
  }, []);

  // Load registrants and existing scores
  const loadData = useCallback(async () => {
    if (!eventId || !token) return;
    setLoading(true);
    try {
      const [eventRes, regRes, scoresRes] = await Promise.all([
        axios.get(`/api/events/${eventId}`, { headers }),
        axios.get(`/api/events/${eventId}/registrants`, { headers }),
        axios.get(`/api/tryout-scoring/${eventId}/scores`, { headers }),
      ]);

      setEventData(eventRes.data?.event || eventRes.data);

      const regs = Array.isArray(regRes.data?.registrants)
        ? regRes.data.registrants
        : Array.isArray(regRes.data) ? regRes.data : [];
      setRegistrants(regs);

      // Mark which registrant IDs the current user has already scored
      const myScores = (scoresRes.data?.scores || []).filter(
        (s) => s.evaluatorId === (user?.userId || user?.id)
      );
      setScoredIds(new Set(myScores.map((s) => s.registrantId)));
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('You are not assigned as an evaluator for this tryout.');
      } else {
        toast.error('Failed to load tryout data.');
      }
    } finally {
      setLoading(false);
    }
  }, [eventId, token, headers, user]);

  useEffect(() => { loadData(); }, [loadData]);

  // Load existing score for the current player whenever the index changes.
  // photoUploadedRef is reset here so a photo uploaded for one player can't
  // bleed into the next. The ref is set to true in handlePhotoCapture so that
  // a stale in-flight GET can't wipe a photo the evaluator just took.
  useEffect(() => {
    const currentReg = registrants[currentIndex];
    if (!currentReg || !eventId || !token) return;

    photoUploadedRef.current = false;
    const controller = new AbortController();

    axios
      .get(`/api/tryout-scoring/${eventId}/my-score/${currentReg.registrantId}`, {
        headers,
        signal: controller.signal,
      })
      .then(({ data }) => {
        if (data.score) {
          setDraft((prev) => ({
            scores: data.score.scores || {},
            overall: data.score.overall ?? null,
            notes: data.score.notes || '',
            photoUrl: photoUploadedRef.current ? prev.photoUrl : (data.score.photo_url || null),
          }));
        } else {
          setDraft((prev) => ({
            scores: {},
            overall: null,
            notes: '',
            photoUrl: photoUploadedRef.current ? prev.photoUrl : null,
          }));
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        setDraft({ scores: {}, overall: null, notes: '', photoUrl: null });
      });

    return () => controller.abort();
  }, [currentIndex, registrants, eventId, token, headers]);

  const currentReg = registrants[currentIndex];

  const playerName = currentReg
    ? currentReg.player?.name ||
      currentReg.user?.name ||
      'Unknown Player'
    : null;

  const setCategoryScore = (key, val) =>
    setDraft((d) => ({ ...d, scores: { ...d.scores, [key]: val } }));

  const handleJumpToNumber = (e) => {
    e.preventDefault();
    const trimmed = numberInput.trim();
    if (!trimmed) return;
    const num = Number(trimmed);
    const idx = registrants.findIndex((r) => r.tryoutNumber === num);
    if (idx === -1) {
      toast.error(`No player with number #${trimmed}`);
      return;
    }
    setCurrentIndex(idx);
    setNumberInput('');
  };

  const handlePhotoCapture = async (e) => {
    const file = e.target.files[0];
    if (!file || !currentReg) return;
    setPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const res = await axios.post(
        `/api/tryout-scoring/${eventId}/scores/${currentReg.registrantId}/photo`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      photoUploadedRef.current = true;
      setDraft((d) => ({ ...d, photoUrl: res.data.photo_url }));
      toast.success('Photo saved!');
    } catch {
      toast.error('Failed to upload photo.');
    } finally {
      setPhotoUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = async () => {
    setDraft((d) => ({ ...d, photoUrl: null }));
    // Persist the removal by saving score with null photo_url
    if (currentReg) {
      try {
        await axios.post(
          `/api/tryout-scoring/${eventId}/scores/${currentReg.registrantId}`,
          { scores: draft.scores, overall: draft.overall, notes: draft.notes, photo_url: null },
          { headers }
        );
      } catch { /* non-critical */ }
    }
  };

  const handleSave = async (andNext = false) => {
    if (!currentReg) return;
    setSaving(true);
    try {
      await axios.post(
        `/api/tryout-scoring/${eventId}/scores/${currentReg.registrantId}`,
        { scores: draft.scores, overall: draft.overall, notes: draft.notes, photo_url: draft.photoUrl },
        { headers }
      );
      setScoredIds((prev) => new Set([...prev, currentReg.registrantId]));
      toast.success('Score saved!');
      if (andNext && currentIndex < registrants.length - 1) {
        setCurrentIndex((i) => i + 1);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save score.');
    } finally {
      setSaving(false);
    }
  };

  const progress = registrants.length
    ? Math.round((scoredIds.size / registrants.length) * 100)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 rounded-full border-2 border-slate-600 border-t-white" />
      </div>
    );
  }

  if (!eventId) {
    return (
      <div className="p-6 text-center text-slate-400">No tryout event specified.</div>
    );
  }

  if (registrants.length === 0) {
    return (
      <div className="p-6 text-center text-slate-400">No registered players found for this tryout.</div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">
          {eventData?.title || 'Tryout Scoring'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Score each player independently. Your scores are private until the admin reviews them.
        </p>
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span>{scoredIds.size} of {registrants.length} players scored</span>
          <span>{progress}%</span>
        </div>
        <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${progress}%`, backgroundColor: 'var(--theme-primary)' }}
          />
        </div>
      </div>

      {/* Player selector dropdown + jump-to-number */}
      <div className="flex gap-2 items-end">
        <div className="flex-1">
          <label htmlFor="tryout-select-player" className="block text-xs font-medium text-gray-500 mb-1">
            Select Player
          </label>
          <select
            id="tryout-select-player"
            value={currentIndex}
            onChange={(e) => setCurrentIndex(Number(e.target.value))}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
          >
            {registrants.map((reg, idx) => {
              const scored = scoredIds.has(reg.registrantId);
              return (
                <option key={reg.registrantId} value={idx}>
                  {scored ? '✓ ' : ''}{playerLabel(reg)}
                </option>
              );
            })}
          </select>
        </div>
        <form onSubmit={handleJumpToNumber} className="w-32">
          <label htmlFor="tryout-jump-number" className="block text-xs font-medium text-gray-500 mb-1">
            Jump to #
          </label>
          <div className="flex gap-1">
            <input
              id="tryout-jump-number"
              type="number"
              inputMode="numeric"
              value={numberInput}
              onChange={(e) => setNumberInput(e.target.value)}
              placeholder="#"
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)]"
            />
            <button
              type="submit"
              aria-label="Jump to player number"
              className="flex-shrink-0 p-2 rounded-lg text-white disabled:opacity-30"
              disabled={!numberInput.trim()}
              style={{ backgroundColor: 'var(--theme-primary)' }}
            >
              <ArrowRightIcon className="h-4 w-4" />
            </button>
          </div>
        </form>
      </div>

      {/* Scoring card */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        {/* Player header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="h-12 w-12 rounded-full flex items-center justify-center text-white font-semibold text-lg flex-shrink-0"
              style={{ backgroundColor: 'var(--theme-primary)' }}
            >
              {currentReg?.tryoutNumber != null ? `#${currentReg.tryoutNumber}` : getInitials(playerName)}
            </div>
            <div>
              <p className="font-semibold text-gray-900 text-lg">{playerName}</p>
              <p className="text-xs text-gray-400">
                Player {currentIndex + 1} of {registrants.length}
                {scoredIds.has(currentReg?.registrantId) && (
                  <span className="ml-2 inline-flex items-center gap-1 text-green-600">
                    <CheckCircleIcon className="h-3.5 w-3.5" /> Scored
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Prev / Next navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
              disabled={currentIndex === 0}
              className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setCurrentIndex((i) => Math.min(registrants.length - 1, i + 1))}
              disabled={currentIndex === registrants.length - 1}
              className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Score inputs */}
        <div className="px-6 py-5 space-y-5 bg-slate-950">
          {CATEGORIES.map(({ key, label }) => (
            <ScoreSlider
              key={key}
              label={label}
              value={draft.scores[key] ?? null}
              onChange={(v) => setCategoryScore(key, v)}
            />
          ))}

          <div className="border-t border-white/10 pt-5">
            <ScoreSlider
              label="Overall Impression"
              value={draft.overall}
              onChange={(v) => setDraft((d) => ({ ...d, overall: v }))}
            />
          </div>

          {/* Memo */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">Memo / Notes</label>
            <textarea
              rows={3}
              value={draft.notes}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              placeholder="Notes about this player (private to you until admin reviews)"
              className="w-full px-3 py-2 text-sm rounded-lg bg-slate-800 border border-white/10 text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] resize-none"
            />
          </div>

          {/* Player photo */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">Player Photo</label>
            {draft.photoUrl ? (
              <div className="relative inline-block">
                <img
                  src={draft.photoUrl}
                  alt="Player"
                  className="h-32 w-32 object-cover rounded-xl border border-white/10"
                />
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow transition-colors"
                  title="Remove photo"
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                disabled={photoUploading}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-700 hover:bg-slate-600 border border-white/10 text-slate-300 text-sm font-medium transition-colors disabled:opacity-50"
              >
                <CameraIcon className="h-5 w-5" />
                {photoUploading ? 'Uploading…' : 'Take / Add Photo'}
              </button>
            )}
            {/* capture="environment" uses the rear camera on phones/tablets */}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoCapture}
              className="hidden"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex gap-3 justify-end">
          <button
            onClick={() => handleSave(false)}
            disabled={saving}
            className="btn-secondary text-sm"
          >
            Save
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving || currentIndex === registrants.length - 1}
            className="btn-primary text-sm"
          >
            {saving ? 'Saving…' : 'Save & Next'}
          </button>
        </div>
      </div>

      {/* Done banner */}
      {scoredIds.size === registrants.length && (
        <div className="rounded-2xl border border-green-200 bg-green-50 px-6 py-4 flex items-center gap-3">
          <CheckCircleSolid className="h-6 w-6 text-green-500 flex-shrink-0" />
          <div>
            <p className="font-semibold text-green-800">All players scored!</p>
            <p className="text-sm text-green-600">The admin can now review and combine all evaluators' scores.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TryoutScoring;
